import { BASE_METHOD, fetchApi, FetchApiOrigin } from "~/common/API/baseApi";

// id каналов разных соцсетей пересекаются: канал однозначно задаёт только пара social + id
export type PlaceSocial = "telegram" | "vk" | "max";

export interface CollectionPlaceRef {
  placeId: number;
  social: PlaceSocial;
}

export interface CollectionPlace {
  id: number;
  placeType: number;
  createdAt: string;
  place: Record<string, any> | null;
}

// Сколько стоит разместиться этим форматом во всех каналах подборки сразу, с комиссией.
// Только форматы, которые есть хотя бы у одного канала; порядок задаёт бэк
export interface FormatPrice {
  priceType: string;
  name: string;
  price: number;
}

export interface Collection {
  id: number;
  name: string;
  slug: string;
  placeCount: number;
  subscribersCount: number;
  // Охват за 24 часа, а не просмотры за всё время
  viewsCount: number;
  formatPrices: FormatPrice[];
  placeCollections: CollectionPlace[];
  createdAt: string;
  isPublic: boolean;
}

export interface CollectionsResponse {
  records: Collection[];
  meta: { page: number; pageSize: number; pageCount: number; totalCount: number };
}

export const COLLECTION_ERROR_CODE = {
  // Удаление: ни одного из переданных каналов в подборке уже нет
  PLACES_NOT_IN_COLLECTION: 3055,
} as const;

export default class CollectionService {
  static getCollections = (params: Record<string, any> = {}) =>
    fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/user/collections",
      params,
    }) as Promise<CollectionsResponse>;

  // silent — без полноэкранного лоадера, для фонового перечитывания
  static getCollection = (id: number, { silent = false } = {}) =>
    fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/user/collection",
      params: { id },
      isFullScreenLoading: !silent,
    }) as Promise<Collection>;

  // Открыта без авторизации: страница по ссылке доступна гостю
  static getPublicCollection = (slug: string) =>
    fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/user/collection/public",
      params: { slug },
    }) as Promise<Collection>;

  static createCollection = (body: { name: string; slug?: string; isPublic?: boolean }) =>
    fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/user/collection",
      method: BASE_METHOD.POST,
      body,
    }) as Promise<Collection>;

  static updateCollection = (body: {
    id: number;
    name?: string;
    slug?: string;
    isPublic?: boolean;
  }) =>
    fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/user/collection",
      method: BASE_METHOD.PUT,
      body,
    }) as Promise<Collection>;

  static deleteCollection = (id: number) =>
    fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/user/collection",
      method: BASE_METHOD.DELETE,
      params: { id },
    });

  // Пачкой: каналы, которые уже есть в подборке, бэк молча пропускает. Канал не из
  // каталога — 404 на весь запрос, ничего не добавится. В ответе подборка с верным
  // placeCount и списком каналов, но охват, подписчики и цены в ней ещё старые:
  // их пересчёт идёт в фоне
  static addPlaces = (collectionId: number, places: CollectionPlaceRef[]) =>
    fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/user/collection-place",
      method: BASE_METHOD.POST,
      body: { collectionId, places },
    }) as Promise<Collection>;

  // collectionId — в query: проверка владельца ищет его там или в JSON-теле.
  // Отсутствующие в подборке каналы пропускаются; если не удалено ничего — 3055
  static removePlaces = (collectionId: number, places: CollectionPlaceRef[]) =>
    fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/user/collection-place",
      method: BASE_METHOD.DELETE,
      params: { collectionId },
      body: { places },
    });

  // Только для вошедшего пользователя
  static getCounters = () =>
    fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/user/counters",
      isFullScreenLoading: false,
    }) as Promise<{ favoritePlacesCount: number; collectionsCount: number }>;
}

// Ключ канала для выделения и сравнения: одного id мало, он повторяется в разных соцсетях
export const placeKey = (place: { id?: number | null; social?: string | null } | null) =>
  `${place?.social || "telegram"}:${Number(place?.id)}`;

export const toPlaceRef = (place: { id: number; social?: string | null }): CollectionPlaceRef => ({
  placeId: Number(place.id),
  social: (place.social || "telegram") as PlaceSocial,
});
